import { configLogger } from "../utils/logger";

interface ApiConfig {
  apiEndpoint: string;
  stage: string;
  impactAnalysisAsyncEnabled?: boolean;
  userPoolId?: string;
  userPoolClientId?: string;
  region?: string;
  marketLanding?: boolean | string;
  stewardConsole?: boolean | string;
  profileSocketEndpoint?: string;
  profileSocketUrl?: string;
  wsEndpoint?: string;
  WS_ENDPOINT?: string;
}

declare global {
  interface Window {
    API_CONFIG?: ApiConfig;
  }
}

function detectEnvironmentFromDomain(): { apiEndpoint: string; stage: string } {
  const hostname = window.location.hostname;

  // Production environment
  if (hostname === "prod.akem.com" || hostname.includes("prod")) {
    return {
      apiEndpoint:
        "https://z53q4cfgn6.execute-api.us-east-1.amazonaws.com/prod/",
      stage: "prod",
    };
  }

  // Staging environment
  if (
    hostname === "staging.akem.com" ||
    hostname.includes("staging") ||
    hostname.includes("stg")
  ) {
    return {
      apiEndpoint:
        "https://6ucpmyl1c9.execute-api.us-east-1.amazonaws.com/stg/",
      stage: "stg",
    };
  }

  // CloudFront distribution URLs - try to detect from URL patterns
  if (hostname.includes("cloudfront.net")) {
    const url = window.location.href;
    if (url.includes("prod") || url.includes("production")) {
      return {
        apiEndpoint:
          "https://z53q4cfgn6.execute-api.us-east-1.amazonaws.com/prod/",
        stage: "prod",
      };
    }
    // Default CloudFront to staging
    return {
      apiEndpoint:
        "https://6ucpmyl1c9.execute-api.us-east-1.amazonaws.com/stg/",
      stage: "stg",
    };
  }

  // Local development fallback - force port 3002 for API
  return {
    apiEndpoint: "http://localhost:3002",
    stage: "dev",
  };
}

// Cache dev config to avoid repeated recomputation and log spam during re-renders
let cachedDevConfig: ApiConfig | undefined;
let loggedDevConfigOnce = false;
let cachedProfileSocketEndpoint: string | undefined;
let resolvedProfileSocketOnce = false;
let loggedSocketFallbackOnce = false;

function normalizeWsEndpoint(value: unknown): string | undefined {
  if (!value) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed) return undefined;

  if (/^wss?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, "");
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed
      .replace(/^https:/i, "wss:")
      .replace(/^http:/i, "ws:")
      .replace(/\/+$/, "");
  }

  if (/^\/{2}/.test(trimmed)) {
    return `wss:${trimmed.replace(/\/+$/, "")}`;
  }

  if (/^[a-z0-9.-]+/i.test(trimmed)) {
    return `wss://${trimmed.replace(/\/+$/, "")}`;
  }

  return undefined;
}

function resolveSocketFromWindow(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const cfg = window.API_CONFIG as Partial<ApiConfig> | undefined;
  const legacy = (window as any).config as
    | (Partial<ApiConfig> & { apiUrl?: string; wsEndpoint?: string })
    | undefined;

  const candidates: Array<unknown> = [];
  if (cfg) {
    candidates.push(
      cfg.profileSocketEndpoint,
      cfg.profileSocketUrl,
      cfg.wsEndpoint,
      cfg.WS_ENDPOINT
    );
  }
  if (legacy) {
    candidates.push(
      legacy.profileSocketEndpoint,
      legacy.profileSocketUrl,
      legacy.wsEndpoint,
      legacy.WS_ENDPOINT
    );
  }

  for (const candidate of candidates) {
    const normalized = normalizeWsEndpoint(candidate);
    if (normalized) return normalized;
  }
  return undefined;
}

function resolveSocketFromEnv(): string | undefined {
  const envCandidates: Array<unknown> = [
    import.meta.env.VITE_PROFILE_SOCKET_ENDPOINT,
    import.meta.env.VITE_PROFILE_SOCKET_URL,
    import.meta.env.VITE_WS_ENDPOINT,
  ];
  for (const candidate of envCandidates) {
    const normalized = normalizeWsEndpoint(candidate);
    if (normalized) return normalized;
  }
  return undefined;
}

function deriveSocketFromApi(apiEndpoint: string | undefined): string | undefined {
  if (!apiEndpoint) return undefined;
  try {
    const url = new URL(apiEndpoint);
    url.protocol = url.protocol === "http:" ? "ws:" : "wss:";
    return url.toString().replace(/\/+$/, "");
  } catch {
    return normalizeWsEndpoint(apiEndpoint);
  }
}

export function getProfileSocketEndpoint(): string | undefined {
  if (resolvedProfileSocketOnce) {
    return cachedProfileSocketEndpoint;
  }

  const envEndpoint = resolveSocketFromEnv();
  if (envEndpoint) {
    cachedProfileSocketEndpoint = envEndpoint;
    resolvedProfileSocketOnce = true;
    return cachedProfileSocketEndpoint;
  }

  const windowEndpoint = resolveSocketFromWindow();
  if (windowEndpoint) {
    cachedProfileSocketEndpoint = windowEndpoint;
    resolvedProfileSocketOnce = true;
    return cachedProfileSocketEndpoint;
  }

  let candidateApiEndpoint: string | undefined;
  if (typeof window !== "undefined" && window.API_CONFIG) {
    const apiEndpoint = (window.API_CONFIG as Partial<ApiConfig>).apiEndpoint;
    if (typeof apiEndpoint === "string" && apiEndpoint.trim()) {
      candidateApiEndpoint = apiEndpoint;
    }
  }

  if (!candidateApiEndpoint && import.meta.env.VITE_API_ENDPOINT) {
    candidateApiEndpoint = import.meta.env.VITE_API_ENDPOINT;
  }

  if (!candidateApiEndpoint && typeof window !== "undefined") {
    try {
      candidateApiEndpoint = detectEnvironmentFromDomain().apiEndpoint;
    } catch {
      candidateApiEndpoint = undefined;
    }
  }

  const derivedEndpoint = deriveSocketFromApi(candidateApiEndpoint);

  if (derivedEndpoint && !loggedSocketFallbackOnce) {
    configLogger.warn("Derived profile socket endpoint from API endpoint", {
      endpoint: derivedEndpoint,
    });
    loggedSocketFallbackOnce = true;
  }

  cachedProfileSocketEndpoint = derivedEndpoint;
  resolvedProfileSocketOnce = true;
  return cachedProfileSocketEndpoint;
}

export function getApiConfig(): ApiConfig {
  // In development, prioritize environment variables over everything and cache
  if (import.meta.env.DEV) {
    if (cachedDevConfig) return cachedDevConfig;

    if (import.meta.env.VITE_API_ENDPOINT) {
      const config: ApiConfig = {
        apiEndpoint: import.meta.env.VITE_API_ENDPOINT,
        stage: import.meta.env.VITE_STAGE || "dev",
        impactAnalysisAsyncEnabled: /^(1|true|yes)$/i.test(
          String(import.meta.env.VITE_IMPACT_ANALYSIS_ASYNC_ENABLED || "")
        ),
        userPoolId: import.meta.env.VITE_USER_POOL_ID,
        userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        region: import.meta.env.VITE_COGNITO_REGION || "us-east-1",
      };
      if (!loggedDevConfigOnce) {
        configLogger.debug("Using environment variables for API config", config);
        loggedDevConfigOnce = true;
      }
      cachedDevConfig = config;
      return cachedDevConfig;
    }

    // Intelligent fallback based on domain/environment detection (dev only)
    const detectedConfig = detectEnvironmentFromDomain();
    const finalConfig: ApiConfig = {
      apiEndpoint:
        import.meta.env.VITE_API_ENDPOINT || detectedConfig.apiEndpoint,
      stage: import.meta.env.VITE_STAGE || detectedConfig.stage,
      impactAnalysisAsyncEnabled: /^(1|true|yes)$/i.test(
        String(import.meta.env.VITE_IMPACT_ANALYSIS_ASYNC_ENABLED || "")
      ),
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      region: import.meta.env.VITE_COGNITO_REGION || "us-east-1",
    };
    if (!loggedDevConfigOnce) {
      configLogger.warn(
        "Using intelligent fallback config - window.API_CONFIG not found",
        {
          detected: detectedConfig,
          final: finalConfig,
          hostname: window.location.hostname,
          href: window.location.href,
        }
      );
      loggedDevConfigOnce = true;
    }
    cachedDevConfig = finalConfig;
    return cachedDevConfig;
  }

  // Production path (no caching; window.API_CONFIG should be present)
  if (window.API_CONFIG) {
    configLogger.debug(
      "Using deployed API config from window.API_CONFIG",
      window.API_CONFIG
    );
    return window.API_CONFIG as ApiConfig;
  }

  // Log error for missing config in production
  configLogger.error(
    "window.API_CONFIG is missing! This should be set by the deployment."
  );

  // Fallback detection (rare in prod)
  const detectedConfig = detectEnvironmentFromDomain();
  const finalConfig: ApiConfig = {
    apiEndpoint:
      import.meta.env.VITE_API_ENDPOINT || detectedConfig.apiEndpoint,
    stage: import.meta.env.VITE_STAGE || detectedConfig.stage,
    impactAnalysisAsyncEnabled: /^(1|true|yes)$/i.test(
      String(import.meta.env.VITE_IMPACT_ANALYSIS_ASYNC_ENABLED || "")
    ),
    userPoolId: import.meta.env.VITE_USER_POOL_ID,
    userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
    region: import.meta.env.VITE_COGNITO_REGION || "us-east-1",
  };
  return finalConfig;
}

export function getApiEndpoint(): string {
  const config = getApiConfig();
  return config.apiEndpoint.endsWith("/")
    ? config.apiEndpoint.slice(0, -1)
    : config.apiEndpoint;
}

export function isImpactAnalysisAsyncEnabled(): boolean {
  try {
    const cfg = getApiConfig();
    return Boolean(cfg.impactAnalysisAsyncEnabled);
  } catch {
    return false;
  }
}
